import { useState, useEffect, useRef } from 'react';
import { Avatar, AvatarFallback } from '@/components/shadcn-ui/avatar';
import { Bell, FileText, Link2 } from 'lucide-react';
import { appAxios } from '@/network/base/appAxios';
import { useUserStore } from '@/stores/useUser.store';
import { useLocation, useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { useProjectStore } from '@/stores/useProject.store';
import { toast } from 'sonner';
import PermitStepperModal from '@/components/permitStepper/PermitStepperModal';
import ContactFormModal from '@/components/sharedComponents/ContactFormModal';

const formatJurisdictionName = code => {
  if (!code) return '';
  return code
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};


const renderBoldText = text => {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*')) {
      return <em key={i}>{part.slice(1, -1)}</em>;
    }
    return part;
  });
};

export function ChatMessage({ message, protectedRoute }) {
  const { auth } = useUserStore.getState();
  const navigate = useNavigate();
  const location = useLocation();
  const { permitChatId } = useParams();
  const permitChatGroups = useProjectStore(state => state.permitChatGroups);
  const activePermitChatID = useProjectStore(state => state.activePermitChatID);
  const ensurePermitChatGroupsLoaded = useProjectStore(state => state.ensurePermitChatGroupsLoaded);
  const getProjectIdByPermitChatId = useProjectStore(state => state.getProjectIdByPermitChatId);
  const userRole = useUserStore(state => state.userRole);

  const isUser = message.role === 'user';
  const [submitted, setSubmitted] = useState(false);
  const [projectId, setProjectId] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [showStepperModal, setShowStepperModal] = useState(false);
  const [showContactFormModal, setShowContactFormModal] = useState(false);

  const shownModalForChatRef = useRef(null);

  useEffect(() => {
    if (!protectedRoute) return;
    const loadProjectId = async () => {
      try {
        setDataLoading(true);
        await ensurePermitChatGroupsLoaded();
        const chatId = permitChatId || activePermitChatID;
        if (chatId) {
          const foundProjectId = getProjectIdByPermitChatId(chatId);
          if (foundProjectId) {
            setProjectId(foundProjectId);
            localStorage.setItem(`permitChat_${chatId}_projectId`, foundProjectId);
          } else {
            const cachedProjectId = localStorage.getItem(`permitChat_${chatId}_projectId`);
            if (cachedProjectId) {
              setProjectId(cachedProjectId);
            }
          }
        }
      } catch (error) {
        console.error('Error loading project data:', error);
      } finally {
        setDataLoading(false);
      }
    };

    loadProjectId();
  }, [
    permitChatId,
    activePermitChatID,
    // permitChatGroups?.length,
    ensurePermitChatGroupsLoaded,
    getProjectIdByPermitChatId,
    protectedRoute,
  ]);

  useEffect(() => {
    if (!projectId) return;

    const currentChatId = permitChatId || activePermitChatID;
    if (!currentChatId) return;
    if (shownModalForChatRef.current !== currentChatId) {
      shownModalForChatRef.current = null;
    }
    if (shownModalForChatRef.current === currentChatId) {
      return;
    }

    const submissionStatus = localStorage.getItem(`permit_submission_status_${projectId}_${permitChatId}`);
    const activePermitChat = permitChatGroups?.find(permit => permit._id === currentChatId);
    // const AlreadySubmit = activePermitChat?.permitSubmitted;
    const AlreadySubmit = activePermitChat?.baseFeesPaid && activePermitChat?.permitSubmitted;

    if (submissionStatus) {
      const { submitted: wasSubmitted } = JSON.parse(submissionStatus);
      if (wasSubmitted) {
        setSubmitted(true);
        shownModalForChatRef.current = currentChatId;
      }
    } else if (AlreadySubmit) {
      setSubmitted(true);
      shownModalForChatRef.current = currentChatId;
    }
  }, [projectId, permitChatId, activePermitChatID]);

  useEffect(() => {
    const currentChatId = permitChatId || activePermitChatID;
    if (currentChatId && shownModalForChatRef.current !== currentChatId && shownModalForChatRef.current !== null) {
      setSubmitted(false);
    }
  }, [permitChatId, activePermitChatID]);

  useEffect(() => {
    if (message.type !== 'submit_button') return;
    const params = new URLSearchParams(location.search);
    if (params.get('showstepper') === 'true') {
      setShowStepperModal(true);
      params.delete('showstepper');
      const newSearch = params.toString();
      navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
      return;
    }
    if (params.get('showauthmodal') !== 'true') return;
    setShowStepperModal(true);
    params.delete('showauthmodal');
    const newSearch = params.toString();
    navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
  }, [location.search, message.type, navigate]);


  const attemptSubmit = () => {
    const fetchedUserInfo = message.metadata?.user_info || null;
    if (!auth) {
      navigate(`/auth?user_info=${encodeURIComponent(JSON.stringify(fetchedUserInfo))}`, { replace: true });
      return;
    }
    setShowStepperModal(true);
  };

  const handleFileClick = async (fileKey) => {
    try {
      const response = await appAxios.get('/ProtectedPermitChat/v1.0/getAttachmentDocument', { params: { key: fileKey } });
      if (response.data?.status === 'Success') {
        window.open(response.data.data.viewUrl, '_blank');
      }
    } catch (error) {
      console.error('Error processing file:', error);
      toast.error('Failed to process file');
    }
  };

  if (message.type === 'submit_button') {
    return (
      <>
        <div className="flex items-center justify-center w-full py-3">
          {protectedRoute ? (
            dataLoading ? (
              <button
                type="button"
                disabled
                className="flex items-center justify-center bg-[#212121] rounded-full px-4 py-2 border border-[#FEBA08] min-w-64 font-normal cursor-not-allowed opacity-50 transition-colors"
              >
                Loading...
              </button>
            ) : !submitted ? (
              <button
                type="button"
                onClick={attemptSubmit}
                className="flex items-center justify-center bg-[#212121] rounded-full px-4 py-2 border border-[#FEBA08] min-w-64 font-normal cursor-pointer hover:bg-[#2a2a2a] transition-colors"
              >
                Click to continue
              </button>
            ) : (
              <button
                type="button"
                disabled
                className="flex items-center justify-center bg-[#212121] rounded-full px-4 py-2 border border-[#FEBA08] min-w-64 font-normal cursor-not-allowed opacity-50 transition-colors"
              >
                <span className="text-green-400">Submitted!</span>
              </button>
            )
          ) : (
            <button
              type="button"
              onClick={attemptSubmit}
              className="flex items-center justify-center bg-[#212121] rounded-full px-4 py-2 border border-[#FEBA08] min-w-64 font-normal cursor-pointer hover:bg-[#2a2a2a] transition-colors"
            >
              Click to continue
            </button>
          )}
        </div>

        <PermitStepperModal
          isOpen={showStepperModal}
          onClose={() => setShowStepperModal(false)}
          permitChatId={permitChatId || activePermitChatID}
          projectId={projectId}
          messageMetadata={message.metadata || {}}
          onComplete={() => {
            setSubmitted(true);
            setShowStepperModal(false);
          }}
        />
      </>
    );
  }

  if (message.type === 'contact_form') {
    const jurisdictionName = formatJurisdictionName(message.metadata?.jurisdiction_code);
    return (
      <>
        <div className="flex items-center justify-center w-full py-3">
          <button
            type="button"
            onClick={() => setShowContactFormModal(true)}
            className="flex items-center justify-center bg-black rounded-full px-4 py-3 min-w-64 font-normal cursor-pointer hover:bg-black/90 transition-colors"
          >
            <div className="flex items-center justify-center">
              <Bell className="w-3 h-3 md:w-4 md:h-4 text-white mr-2" />
              {jurisdictionName ?
                <span className="text-sm text-white">Notify Me When You Launch In {jurisdictionName}</span> :
                <span className="text-sm text-white">Notify Me When You Launch</span>
              }
            </div>
          </button>
        </div>
        <ContactFormModal
          isOpen={showContactFormModal}
          onClose={() => setShowContactFormModal(false)}
          chatId={permitChatId}
          inquiryType="wishlist" />
      </>
    );
  }

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      {!isUser && (
        <Avatar className="size-10 bg-black">
          <AvatarFallback>
            <img src="/kelly.png" alt="Kelly Avatar" className="size-10 object-cover bg-black" />
          </AvatarFallback>
        </Avatar>
      )}

      {isUser ? (
        <div className="flex flex-col gap-2 max-w-[80%]">

          {message?.content?.attachments && message.content.attachments.length > 0 && (
            <div className="flex flex-col gap-2">
              {message.content.attachments.map((file, index) => {
                const isUploading = !file.key || !file.fileUrl;
                return (
                  <button
                    key={index}
                    onClick={() => !isUploading && handleFileClick(file.key)}
                    disabled={isUploading}
                    className={`flex flex-col bg-neutral-900 rounded-lg overflow-hidden transition-colors border border-neutral-700 ${isUploading ? 'opacity-60 cursor-wait' : 'hover:bg-neutral-800 cursor-pointer'}`}
                  >
                    <div className="flex items-center gap-3 px-3 pt-3 pb-2">
                      <div className="flex-shrink-0 w-9 h-9 bg-red-500 rounded-md flex items-center justify-center">
                        <FileText className="w-4 h-4 text-white" />
                      </div>
                      <p className="flex-1 text-sm text-white font-medium truncate text-left">{file.name}</p>
                      {isUploading ? (
                        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                      ) : (
                        <Link2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      )}
                    </div>
                    {file.document_name && (
                      <div className="px-3 py-1.5 border-t border-neutral-700 bg-neutral-800/50">
                        <p className="text-xs italic text-gray-400 truncate text-left">{file.document_name}</p>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {(message?.content?.content && message?.content?.content !== "Attachments uploaded") && (
            <div className="rounded-lg p-3 text-white bg-black">
              <p className="text-sm whitespace-pre-wrap">{renderBoldText(message?.content?.content)}</p>
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-lg p-3 max-w-[80%] text-white bg-neutral-800">
          <p className="text-sm whitespace-pre-wrap">{renderBoldText(message?.content?.content)}</p>
        </div>
      )}
    </div>
  );
}